/**
 * Navigation utilities
 *
 * Pure utility functions for navigation logic.
 * Supports both sidebar (desktop) and bottom nav (mobile).
 */
import type { LucideIcon } from "lucide-react";
import { Home, Calendar, TrendingUp, NotebookPen, Sparkles } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

/** Navigation item configuration */
export interface NavItem {
  /** Translation key resolved via `t()` at render time (e.g. "nav.dashboard") */
  labelKey: string;
  /** Route path */
  path: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Optional: highlight this item (e.g., AI Check-in) */
  highlight?: boolean;
}

/** Navigation item type identifier */
export type NavItemType =
  | "dashboard"
  | "ai_check_in"
  | "timeline"
  | "events"
  | "trends";

// ============================================================================
// Constants
// ============================================================================

/** Navigation paths */
export const NAV_PATHS = {
  DASHBOARD: "/",
  AI_CHECK_IN: "/ai-check-in",
  TIMELINE: "/timeline",
  EVENTS: "/events/new",
  TRENDS: "/trends",
} as const;

/** Navigation translation keys (resolved via common namespace `t()`) */
export const NAV_KEYS = {
  DASHBOARD: "nav.dashboard",
  DASHBOARD_SHORT: "nav.home",
  AI_CHECK_IN: "nav.aiCheckIn",
  AI_CHECK_IN_SHORT: "nav.aiCheckInShort",
  TIMELINE: "nav.timeline",
  EVENTS: "nav.journal",
  TRENDS: "nav.trends",
} as const;

/**
 * Sidebar navigation items (desktop)
 * Order: Dashboard, AI Check-in, Timeline, Events, Trends
 */
export const SIDEBAR_NAV_ITEMS: NavItem[] = [
  { labelKey: NAV_KEYS.DASHBOARD, path: NAV_PATHS.DASHBOARD, icon: Home },
  {
    labelKey: NAV_KEYS.AI_CHECK_IN,
    path: NAV_PATHS.AI_CHECK_IN,
    icon: Sparkles,
    highlight: true,
  },
  { labelKey: NAV_KEYS.TIMELINE, path: NAV_PATHS.TIMELINE, icon: Calendar },
  { labelKey: NAV_KEYS.EVENTS, path: NAV_PATHS.EVENTS, icon: NotebookPen },
  { labelKey: NAV_KEYS.TRENDS, path: NAV_PATHS.TRENDS, icon: TrendingUp },
];

/**
 * Bottom navigation items (mobile)
 * Order: Home, Timeline, AI (center), Events, Trends
 * Uses shorter labels for mobile
 */
export const BOTTOM_NAV_ITEMS: NavItem[] = [
  { labelKey: NAV_KEYS.DASHBOARD_SHORT, path: NAV_PATHS.DASHBOARD, icon: Home },
  { labelKey: NAV_KEYS.TIMELINE, path: NAV_PATHS.TIMELINE, icon: Calendar },
  {
    labelKey: NAV_KEYS.AI_CHECK_IN_SHORT,
    path: NAV_PATHS.AI_CHECK_IN,
    icon: Sparkles,
    highlight: true,
  },
  { labelKey: NAV_KEYS.EVENTS, path: NAV_PATHS.EVENTS, icon: NotebookPen },
  { labelKey: NAV_KEYS.TRENDS, path: NAV_PATHS.TRENDS, icon: TrendingUp },
];

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Check if a navigation path is active based on current location
 *
 * @param currentPath - Current browser location pathname
 * @param itemPath - Navigation item's path to check
 * @returns true if the path is active
 *
 * Rules:
 * - Root path "/" is only active when exact match
 * - Other paths are active if currentPath starts with itemPath
 *
 * @example
 * isActive("/", "/") // true
 * isActive("/timeline", "/") // false
 * isActive("/timeline/daily/2026-01-12", "/timeline") // true
 */
export function isActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === "/") {
    return currentPath === "/";
  }
  return currentPath.startsWith(itemPath);
}

/**
 * Get the navigation item for a specific type
 *
 * @param items - Array of navigation items
 * @param path - Path to find
 * @returns The matching NavItem or undefined
 */
export function getNavItemByPath(
  items: readonly NavItem[],
  path: string
): NavItem | undefined {
  return items.find((item) => item.path === path);
}

/**
 * Get highlighted navigation items (e.g., AI Check-in)
 *
 * @param items - Array of navigation items
 * @returns Array of highlighted items
 */
export function getHighlightedItems(items: readonly NavItem[]): NavItem[] {
  return items.filter((item) => item.highlight);
}

/**
 * Check if a navigation item should have special styling
 *
 * @param item - Navigation item to check
 * @returns true if item should be highlighted
 */
export function isHighlightedItem(item: NavItem): boolean {
  return item.highlight === true;
}

/**
 * Get CSS classes for an active navigation item
 *
 * @param active - Whether the item is currently active
 * @param highlighted - Whether the item should be highlighted
 * @returns CSS class string for active state
 */
export function getActiveClasses(active: boolean, highlighted: boolean): string {
  if (highlighted && active) {
    return "bg-primary/10 text-primary";
  }
  if (active) {
    return "bg-accent text-accent-foreground";
  }
  return "";
}

/**
 * Get CSS classes for navigation item hover state
 *
 * @param highlighted - Whether the item should be highlighted
 * @returns CSS class string for hover state
 */
export function getHoverClasses(highlighted: boolean): string {
  if (highlighted) {
    return "hover:bg-primary/10 hover:text-primary";
  }
  return "hover:bg-accent hover:text-accent-foreground";
}

/**
 * Get the index of the center item in bottom nav (for mobile)
 * The center item is typically the primary action (AI Check-in)
 *
 * @param items - Array of navigation items
 * @returns Index of the center item
 */
export function getCenterItemIndex(items: readonly NavItem[]): number {
  return Math.floor(items.length / 2);
}

/**
 * Check if a navigation item is the center item in bottom nav
 *
 * @param items - Array of navigation items
 * @param index - Index to check
 * @returns true if the item is at the center position
 */
export function isCenterItem(items: readonly NavItem[], index: number): boolean {
  return index === getCenterItemIndex(items);
}

// ============================================================================
// Top Navigation Bar Utilities
// ============================================================================

/**
 * Top navigation items for desktop (same as sidebar items)
 * These items appear in the top navigation bar on desktop (lg+ screens)
 */
export const TOP_NAV_ITEMS: NavItem[] = SIDEBAR_NAV_ITEMS;

/**
 * Get the navigation items grouped for top nav layout
 * Returns: { main: non-highlighted items, primary: highlighted items }
 *
 * @param items - Array of navigation items
 * @returns Object with main (non-highlighted) and primary (highlighted) items
 */
export function groupNavItemsForTopNav(
  items: readonly NavItem[]
): { main: NavItem[]; primary: NavItem[] } {
  const main: NavItem[] = [];
  const primary: NavItem[] = [];

  for (const item of items) {
    if (item.highlight) {
      primary.push(item);
    } else {
      main.push(item);
    }
  }

  return { main, primary };
}

/**
 * Get CSS classes for top nav link
 *
 * V1-inspired: clean pill shapes, subtle glow on primary action,
 * refined transitions with slight scale feedback on hover.
 * Motion-safe guards ensure reduced-motion users get no animation.
 *
 * @param active - Whether the item is currently active
 * @param highlighted - Whether the item should be highlighted (primary action)
 * @returns CSS class string
 */
export function getTopNavLinkClasses(
  active: boolean,
  highlighted: boolean
): string {
  const base = "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 motion-safe:transition-all";

  if (highlighted) {
    // Primary action button styling (AI Check-in) -- teal glow on active
    return active
      ? `${base} bg-primary text-primary-foreground shadow-sm shadow-primary/25`
      : `${base} bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-sm hover:shadow-primary/20 motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.98]`;
  }

  // Regular navigation link styling -- subtle active indicator
  return active
    ? `${base} text-foreground bg-accent ring-1 ring-border/50`
    : `${base} text-muted-foreground hover:text-foreground hover:bg-accent`;
}

/**
 * Get CSS classes for top nav container
 * Handles responsive visibility (hidden on mobile, visible on lg+)
 *
 * @returns CSS class string for the container
 */
export function getTopNavContainerClasses(): string {
  return "hidden lg:flex items-center gap-0.5 rounded-lg bg-muted/40 p-1 motion-safe:transition-colors motion-safe:duration-200";
}

/**
 * Get CSS classes for top nav primary action container
 *
 * @returns CSS class string for the primary actions container
 */
export function getTopNavPrimaryContainerClasses(): string {
  return "hidden lg:flex items-center pl-3 ml-3 border-l border-border/40 motion-safe:transition-colors motion-safe:duration-200";
}

/**
 * Get CSS classes for header with top nav
 * Glass morphism header: translucent background + backdrop blur + subtle border
 * Motion-safe transition for background on scroll/theme changes
 *
 * @returns CSS class string for the header
 */
export function getHeaderWithNavClasses(): string {
  return "sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 motion-safe:transition-[background-color,border-color] motion-safe:duration-200";
}

/**
 * Get CSS classes for header inner container
 *
 * @returns CSS class string
 */
export function getHeaderInnerClasses(): string {
  return "flex h-14 items-center justify-between px-4 lg:px-6 mx-auto max-w-screen-2xl";
}

/**
 * Get CSS classes for left section of header (logo + nav)
 *
 * @returns CSS class string
 */
export function getHeaderLeftSectionClasses(): string {
  return "flex items-center gap-8 min-w-0";
}

/**
 * Get CSS classes for right section of header (controls)
 *
 * @returns CSS class string
 */
export function getHeaderRightSectionClasses(): string {
  return "flex items-center gap-3 shrink-0";
}
